import type { LumpsumPlanStatus } from '../../../api/common.types'
import { isStrategyInProgress, shouldShowStrategyShell } from '../shared/planStatus'
import { isPlanDropDelayed, usePlanCountdown } from '../shared/usePlanCountdown'
import { IDEAL_FEATURE_ROWS } from '../shared/idealFeatureRows'
import {
  TimerPanelCountdownSection,
  TimerPanelFeatureRows,
  TimerPanelFooterCopy,
  TimerPanelFooterHeading,
  TimerPanelShell,
  TimerPanelStrategySection
} from '../shared/TimerPanelShell'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { getPlansFlowTheme, PlansThemeFlow } from '../../header/common/riskProfileHeaderTheme'
import { primeTimerCountdownChrome } from '../shared/timerCountdownChrome'

const FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.IDEAL)

type IdealTimerPanelProps = {
  planStatus?: LumpsumPlanStatus | null
  timeToPlanDrop?: number | null
  isSubscriptionEligible: boolean
  strategyCta?: unknown
  refetchPlan: () => void
}

/** Strategy / timer / delayed shell before PLAN_ROLLED_OUT. */
export function IdealTimerPanel({
  planStatus,
  timeToPlanDrop,
  isSubscriptionEligible,
  strategyCta,
  refetchPlan
}: IdealTimerPanelProps) {
  const showStrategy = shouldShowStrategyShell(planStatus)
  const isDelayed = !showStrategy && isPlanDropDelayed(timeToPlanDrop)
  const countdownParts = usePlanCountdown(showStrategy || isDelayed ? 0 : timeToPlanDrop)

  return (
    <TimerPanelShell
      background={FLOW_THEME.chromeBackground}
      title="Ideal Portfolio"
      description="See what your portfolio looks like when it's personalised to your life stage and risk appetite."
      hero={
        showStrategy ? (
          <TimerPanelStrategySection
            promptClassName={FLOW_THEME.primaryDarkTextClassName}
            variant={isStrategyInProgress(planStatus) ? 'complete' : 'find'}
            strategyCta={strategyCta}
            refetchPlan={refetchPlan}
            analyticsScreen={PlansScreen.LumpsumIdealTimer}
          />
        ) : (
          <TimerPanelCountdownSection
            {...primeTimerCountdownChrome(FLOW_THEME, {
              readyIn: 'Portfolio under review, your plan will be ready in',
              delayTitle: 'Your ideal portfolio plan is delayed',
              delayBody:
                "We need extra time to prepare your Ideal Portfolio plan. We'll update you as soon as it's ready.",
              unlockPrompt: "Unlock your ideal portfolio once it's ready"
            })}
            countdownParts={countdownParts}
            isDelayed={isDelayed}
            isSubscriptionEligible={isSubscriptionEligible}
            unlockButtonLabel="Unlock with Prime"
            landingPage="prime"
            analyticsScreen={PlansScreen.LumpsumIdealTimer}
          />
        )
      }
      footer={
        <>
          <TimerPanelFooterHeading>See what your ideal portfolio looks like</TimerPanelFooterHeading>
          <TimerPanelFooterCopy>
            We show you the big picture before you make a single move. See what your portfolio will
            look like once you act on all your recommendations.
          </TimerPanelFooterCopy>
          <TimerPanelFeatureRows rows={IDEAL_FEATURE_ROWS} iconColor={FLOW_THEME.primary} />
        </>
      }
    />
  )
}
